import React, {
  useCallback,
  useMemo,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';

import { Button, Typography } from '@material-ui/core';
import { Edit } from '@material-ui/icons';
import CachedIcon from '@material-ui/icons/Cached';
import { parseISO, format } from 'date-fns';
import { isArray } from 'lodash';
import moment from 'moment';

import { UnidataTable } from '~/components';
import { TRR_CHILE } from '~/constants/modules';
import { t, can, number } from '~/helpers';
import dateTime from '~/hooks/DateTime';
import api from '~/services/api';
import {
  deleteData,
  findData,
  recordFiltering,
  updateData,
} from '~/store/modules/retransregisintegracao/actions';

import DialogConfirmation from './components/DialogConfirmation';

function Retransregisintegracao() {
  const dispatch = useDispatch();
  const formRef = useRef(null);

  // const retransregisintegracao = useSelector(
  //   (state) => state.retransregisintegracao
  // );

  const redux = useSelector((state) => state.retransregisintegracao);

  const { opened } = useSelector((state) => state.dialog);

  const status = useMemo(
    () => [
      { label: 'N', value: t('s.geral.opcoes.naoenviado') },
      { label: 'S', value: t('s.geral.opcoes.liberadoenvio') },
      { label: 'E', value: t('s.geral.opcoes.enviado') },
      { label: 'P', value: t('s.geral.opcoes.pendente') },
    ],
    []
  );

  const [serverDate, setServerDate] = useState(null);
  // const [modal, setModal] = useState(false);
  // const [params, setParams] = useState({});
  const [isUpdate, setIsUpdate] = useState(false);

  const [isFinalizado, setIsFinalizado] = useState(false);
  const [diasintegracaocliente, setDiasintegracaocliente] = useState(0);
  const [diasIntegracaoLoaded, setDiasIntegracaoLoaded] = useState(false);

  const dateTimeFilterProps = useMemo(() => {
    if (!serverDate || diasintegracaocliente <= 0) return {};

    const minDate = moment(serverDate).subtract(diasintegracaocliente, 'days');
    const maxDate = moment(serverDate);

    return {
      minDate,
      maxDate,
      shouldDisableDate: (day) =>
        day.isBefore(minDate, 'day') || day.isAfter(maxDate, 'day'),
      disableMaskedInput: true,
      helperText: '',
    };
  }, [serverDate, diasintegracaocliente]);

  const header = useMemo(() => {
    const data = [
      {
        id: 'datahorainicialmovimentacao',
        label: t('s.operacao.abastecimento.datahorainicialmovimentacao'),
        formatted: (value) => format(parseISO(value), 'dd/MM/yyyy HH:mm'),
        filter: {
          type: 'initialdatetime',
          defaultValue: moment(serverDate || new Date()).format(
            'DD/MM/YYYY 00:00:00'
          ),
          ...dateTimeFilterProps,
        },
      },
      {
        id: 'datahorafinalmovimentacao',
        label: t('s.operacao.abastecimento.datahorafinalmovimentacao'),
        formatted: (value) => format(parseISO(value), 'dd/MM/yyyy HH:mm'),
        filter: {
          type: 'finaldatetime',
          defaultValue: moment(serverDate || new Date()).format(
            'DD/MM/YYYY 23:59:59'
          ),
        },
      },
      {
        id: 'nomeabreviadoposto',
        label: t('s.operacao.abastecimento.nomeposto'),
        filter: { type: 'text' },
      },
      {
        id: 'idbico',
        label: t('s.operacao.abastecimento.idbico'),
        filter: { type: 'number' },
        align: 'left',
      },
      {
        id: 'nomeproduto',
        label: t('s.operacao.abastecimento.nomeproduto'),
        filter: { type: 'text' },
      },
      {
        id: 'tipoMovimento',
        label: t('s.geral.camposistema.tipoMovimento'),
        filter: { type: 'text' },
      },

      {
        id: 'gerainterfacemovimentacao',
        label: t(
          's.operacao.raizenerrologintegracao.gerainterfaceraizenerrologintegracao'
        ),
        styled: (row) => {
          if (row.gerainterfacemovimentacao === 'S') {
            return { color: '#155724', fontWeight: 'bold' }; // verde
          }
          if (row.gerainterfacemovimentacao === 'P') {
            return { color: '#721c24', fontWeight: 'bold' }; // vermelho - primeira
          }
          if (row.gerainterfacemovimentacao === '2') {
            return { color: '#721c24', fontWeight: 'bold' }; // vermelho - segunda repeticao
          }
          if (row.gerainterfacemovimentacao === '3') {
            return { color: '#721c24', fontWeight: 'bold' }; // vermelho - terceira repeticao
          }
          if (row.gerainterfacemovimentacao === '4') {
            return { color: '#721c24', fontWeight: 'bold' }; // vermelho - quarta repeticao - nao repete no modo automatico
          }
          if (row.gerainterfacemovimentacao === 'E') {
            return { color: '#721c24', fontWeight: 'bold' }; // vermelho
          }
          return { color: '#856404', fontWeight: 'normal' }; // amarelo
        },
        // formatted: (value) => status.find((s) => s.label === value).value || '',
        formatted: (value) => {
          if (value && (value === '2' || value === '3' || value === '4')) {
            value = 'P';
          }
          const item = status.find((s) => s.label === value);
          return item ? item.value : '';
        },
        filter: { type: 'select', options: status },
        onUpdate: (row) => {
          alert(
            `Você clicou para atualizar ${row.gerainterfacemovimentacaostr}`
          );
        },
      },

      {
        id: 'volumemovimentacao',
        label: t('s.operacao.abastecimento.volumemovimentacao'),
        align: 'right',
        formatted: (value) => number(value, 2),
      },
    ];

    if (!can(TRR_CHILE)) {
      data.splice(5, 0, {
        id: 'idveiculo',
        label: t('s.operacao.abastecimento.idveiculo'),
        filter: { type: 'text' },
      });
      data.splice(6, 0, {
        id: 'totalizadormovimentacaoabastecimento',
        label: t(
          's.operacao.abastecimento.totalizadormovimentacaoabastecimento'
        ),
        align: 'right',
        formatted: (value) => number(value, 2),
      });
    }
    if (can(TRR_CHILE)) {
      data.splice(5, 0, {
        id: 'codigonomeempresaveiculo',
        label: t('s.cadastro.empresacliente'),
        filter: { type: 'text' },
      });
    }

    return data;
  }, [serverDate, dateTimeFilterProps]);

  const actionsButtons = useMemo(() => {
    const buttons = [];
    isFinalizado && // TEste
      buttons.push({
        title: t('s.geral.botaoalterar'),
        action: (value) => formRef.current.handleOpen(value),
        // action: (value) => handleOpenModalEdit(value),
        icon: <Edit />,
      });

    return buttons;
  }, [isUpdate, isFinalizado]);

  function Toolbar() {
    return (
      <Button
        variant="contained"
        size="small"
        color="primary"
        startIcon={<CachedIcon />}
        onClick={() => handleRefresh()}
      >
        {t('Atualizar')}
      </Button>
    );
  }

  useEffect(() => {
    async function handleGetDate() {
      const date = await dateTime();
      setServerDate(date);
    }

    handleGetDate();
  }, []);

  useEffect(() => {
    async function handleGetDiasIntegracao() {
      try {
        const { data } = await api.get('retransregisintegracao/diasintegracao');
        const days = Number(data);

        setDiasintegracaocliente(Number.isNaN(days) ? 0 : days);
      } catch (errors) {
        if (isArray(errors)) errors.map((error) => toast.error(t(error)));
        else toast.error(t(errors));
      } finally {
        setDiasIntegracaoLoaded(true);
      }
    }

    handleGetDiasIntegracao();
  }, []);

  const handleFetchData = useCallback(async () => {
    try {
      if (!redux.loading) {
        dispatch(recordFiltering(redux.params));
      }
    } catch (errors) {
      if (isArray(errors)) errors.map((error) => toast.error(t(error)));
      else toast.error(t(errors));
    }
  }, [redux]);

  const handleRefresh = useCallback(async () => {
    try {
      if (!redux.loading) {
        dispatch(recordFiltering(redux.params));
      }
    } catch (errors) {
      if (isArray(errors)) errors.map((error) => toast.error(t(error)));
      else toast.error(t(errors));
    }
  }, [redux]);

  // useEffect(() => {
  //   const isTime = setInterval(() => {
  //     if (!opened) {
  //       try {
  //         if (redux.params ) {
  //           if (!redux.loading) {
  //             dispatch(recordFiltering(redux.params));
  //           }
  //         }
  //       } catch (errors) {
  //         if (Array.isArray(errors)) {
  //           errors.forEach((error) => toast.error(t(error)));
  //         } else {
  //           toast.error(t(errors));
  //         }
  //       }
  //     }
  //   }, 60000);

  //   return () => clearInterval(isTime);
  // }, [redux.params, redux.loading, opened]);

  /** AUTO REFRESH otimizado * */
  const reduxRef = useRef(redux);
  const openedRef = useRef(opened);

  useEffect(() => {
    reduxRef.current = redux;
    openedRef.current = opened;
  }, [redux, opened]);

  const handleAutoRefresh = useCallback(() => {
    const currentOpened = openedRef.current;
    const currentRedux = reduxRef.current;
    if (!currentOpened && !currentRedux.loading) {
      try {
        dispatch(recordFiltering(currentRedux.params));
      } catch (errors) {
        if (Array.isArray(errors)) {
          errors.forEach((error) => toast.error(t(error)));
        } else {
          toast.error(t(errors));
        }
      }
    }
  }, [dispatch, t]);

  useEffect(() => {
    const interval = setInterval(() => handleAutoRefresh(), 60000);
    return () => clearInterval(interval);
  }, [handleAutoRefresh]);

  const handleUpdate = React.useCallback(() => {
    dispatch(recordFiltering(redux.params));
  }, [redux.params]);

  return (
    <>
      {serverDate && diasIntegracaoLoaded && (
        <>
          <UnidataTable
            fixed
            create={false}
            update={false}
            redux={redux}
            header={header}
            mensagem={
              diasintegracaocliente > 0
                ? t('s.geral.periodoretransmissao').replace(
                    '%d',
                    diasintegracaocliente
                  )
                : ''
            }
            read={recordFiltering}
            find={findData}
            remove={deleteData}
            toolbar={Toolbar}
            actions={actionsButtons}
            // actions={actions}
            detach="nomeposto"
            keys={['idmovimentacao', 'idposto']}
            onRow={({
              // idposto,
              gerainterfacemovimentacao,
            }) => {
              // setIsUpdate(Number(finalizadadescarganf) === 0);
              setIsFinalizado(
                gerainterfacemovimentacao === 'E' ||
                  gerainterfacemovimentacao === 'P' ||
                  gerainterfacemovimentacao === '2' ||
                  gerainterfacemovimentacao === '3' ||
                  gerainterfacemovimentacao === '4'
              );
              // setIdposto(idposto);
            }}
          />
        </>
      )}

      <DialogConfirmation
        ref={formRef}
        keys={['idmovimentacao,idposto,idatendimento']}
        title="Justificativa para reenvio"
        redux={updateData}
        query={redux.params}
        params={redux.params}
      />
    </>
  );
}

export default Retransregisintegracao;
